import { BoardSystem, type GridPosition, type LockedDoor, type BlockadeWall } from './BoardSystem';
import type { AttackerData } from '../entities/Attacker';
import { isExhausted } from '../entities/Attacker';

export type FiringDirection = 'right' | 'left' | 'down' | 'up';

export interface FiringLine {
  direction: FiringDirection;
  row?: number; // for left/right firing
  col?: number; // for up/down firing
}

export interface ShotEvent {
  attackerId: number;
  orbIndex: number;
  target: GridPosition;
  colorIndex: number;
  /** For snake blocks: the actual cell that was removed (bottom of snake). */
  snakeRemovalTarget?: GridPosition;
  /** For mega blocks: the ID of the mega block that was hit. */
  megaBlockId?: number;
  /** For mega blocks: remaining HP after the hit (0 = destroyed). */
  megaBlockHpRemaining?: number;
  /** Locked doors that were unlocked as a result of this shot (HP reached 0). */
  unlockedDoors?: LockedDoor[];
  /** If this shot hit a blockade wall instead of a cube. */
  blockadeWallHit?: {
    wallId: number;
    remainingHp: number;
    destroyed: boolean;
    wall: BlockadeWall;
  };
}

export class ShootingSystem {
  private board: BoardSystem;

  constructor(board: BoardSystem) {
    this.board = board;
  }

  /**
   * Find the first cube along a perpendicular firing line.
   * Returns the target only if the first visible cube matches the given color.
   * If a different-colored cube blocks the line, returns null.
   * Snake cells block horizontal and upward shots; they can only be hit from above (down).
   * Cells hidden behind locked doors are invisible to shots (skipped).
   */
  findPerpendicularTarget(colorIndex: number, line: FiringLine): GridPosition | null {
    if (line.direction === 'right' && line.row !== undefined) {
      for (let col = 0; col < this.board.cols; col++) {
        // Skip cells behind locked doors or barricades — shots pass through
        if (this.board.isDoorCell(line.row, col)) continue;
        if (this.board.isBarricadeCell(line.row, col)) continue;
        const topColor = this.board.getTopGameplayColor(line.row, col);
        if (topColor !== null) {
          // Snake cells block horizontal shots entirely
          if (this.board.isSnakeCell(line.row, col)) return null;
          return topColor === colorIndex ? { row: line.row, col } : null;
        }
      }
    } else if (line.direction === 'left' && line.row !== undefined) {
      for (let col = this.board.cols - 1; col >= 0; col--) {
        if (this.board.isDoorCell(line.row, col)) continue;
        if (this.board.isBarricadeCell(line.row, col)) continue;
        const topColor = this.board.getTopGameplayColor(line.row, col);
        if (topColor !== null) {
          if (this.board.isSnakeCell(line.row, col)) return null;
          return topColor === colorIndex ? { row: line.row, col } : null;
        }
      }
    } else if (line.direction === 'down' && line.col !== undefined) {
      for (let row = 0; row < this.board.rows; row++) {
        if (this.board.isDoorCell(row, line.col)) continue;
        if (this.board.isBarricadeCell(row, line.col)) continue;
        const topColor = this.board.getTopGameplayColor(row, line.col);
        if (topColor !== null) {
          // Snake cells CAN be hit from above
          return topColor === colorIndex ? { row, col: line.col } : null;
        }
      }
    } else if (line.direction === 'up' && line.col !== undefined) {
      for (let row = this.board.rows - 1; row >= 0; row--) {
        if (this.board.isDoorCell(row, line.col)) continue;
        if (this.board.isBarricadeCell(row, line.col)) continue;
        const topColor = this.board.getTopGameplayColor(row, line.col);
        if (topColor !== null) {
          // Snake cells block upward shots
          if (this.board.isSnakeCell(row, line.col)) return null;
          return topColor === colorIndex ? { row, col: line.col } : null;
        }
      }
    }

    return null;
  }

  /**
   * Get a target position at the board edge for a blockade wall hit animation.
   * Returns a GridPosition near the edge of the board on the given side.
   */
  private getWallEdgeTarget(side: 'top' | 'bottom' | 'left' | 'right', cellIndex: number): GridPosition {
    switch (side) {
      case 'left':   return { row: cellIndex, col: 0 };
      case 'right':  return { row: cellIndex, col: this.board.cols - 1 };
      case 'top':    return { row: 0, col: cellIndex };
      case 'bottom': return { row: this.board.rows - 1, col: cellIndex };
    }
  }

  /**
   * Check whether any cubes of the given color remain on the board,
   * or if any blockade walls of the given color still exist.
   */
  hasTargets(colorIndex: number): boolean {
    if (this.board.findTopCubesByColor(colorIndex).length > 0) return true;
    // Also check for blockade walls of this color
    for (const wall of this.board.getActiveBlockadeWalls().values()) {
      if (wall.colorIndex === colorIndex) return true;
    }
    return false;
  }

  /**
   * Map a firing direction to the board side the wall would be on (between attacker and board).
   * Attacker on left fires 'right' → wall on 'left' side blocks.
   */
  private static wallSideForDirection(direction: FiringDirection): 'top' | 'bottom' | 'left' | 'right' {
    switch (direction) {
      case 'right': return 'left';   // attacker on left, wall on left edge
      case 'left':  return 'right';  // attacker on right, wall on right edge
      case 'down':  return 'top';    // attacker on top, wall on top edge
      case 'up':    return 'bottom'; // attacker on bottom, wall on bottom edge
    }
  }

  /**
   * Fire a perpendicular shot from a specific orb of the attacker.
   * Returns null if no valid target (blocked, empty line, or orb has no ammo).
   * For snake blocks, removes the bottom cell instead of the top.
   * After clearing a cube, decrements HP on any locked doors matching the cube's color.
   * Blockade walls: if a wall blocks the shot path and colors match, the wall is hit instead of cubes.
   * If colors don't match, the shot is blocked entirely (returns null).
   */
  firePerpendicularShot(attacker: AttackerData, orbIndex: number, line: FiringLine): ShotEvent | null {
    const orb = attacker.orbs[orbIndex];
    if (!orb || orb.ammo <= 0) return null;

    // Check for blockade walls blocking this shot
    const wallSide = ShootingSystem.wallSideForDirection(line.direction);
    const cellIndex = (line.direction === 'right' || line.direction === 'left') ? line.row : line.col;
    if (cellIndex === undefined) return null;
    const wallBlock = this.board.isBlockedByWall(wallSide, cellIndex);

    if (wallBlock) {
      if (orb.colorIndex === wallBlock.colorIndex) {
        // Matching color: hit the wall, consume ammo
        const hitResult = this.board.hitBlockadeWall(wallBlock.wallId);
        if (!hitResult) return null;

        orb.ammo--;

        // Get wall data for animation (may have been deleted if destroyed)
        const wall = this.board.getBlockadeWall(wallBlock.wallId);

        if (isExhausted(attacker)) {
          attacker.state = 'exhausted';
        }

        // Use a target position at the board edge for animation purposes
        const edgeTarget = this.getWallEdgeTarget(wallSide, cellIndex);

        return {
          attackerId: attacker.id,
          orbIndex,
          target: edgeTarget,
          colorIndex: orb.colorIndex,
          blockadeWallHit: {
            wallId: wallBlock.wallId,
            remainingHp: hitResult.remainingHp,
            destroyed: hitResult.destroyed,
            wall: wall ?? (() => {
              if (import.meta.env?.DEV) console.warn(`BlockadeWall ${wallBlock.wallId} already removed; using fallback`);
              return {
                id: wallBlock.wallId,
                side: wallSide,
                colorIndex: wallBlock.colorIndex,
                hp: 0,
                maxHp: 0,
                startCell: 0,
                length: 0,
              };
            })(),
          },
        };
      } else {
        // Non-matching color: shot is blocked entirely, no ammo consumed
        return null;
      }
    }

    const target = this.findPerpendicularTarget(orb.colorIndex, line);
    if (!target) return null;

    let snakeRemovalTarget: GridPosition | undefined;
    let megaBlockId: number | undefined;
    let megaBlockHpRemaining: number | undefined;

    const mbId = this.board.getMegaBlockId(target.row, target.col);
    if (mbId !== undefined) {
      // Mega block: decrement HP instead of removing cell
      const mb = this.board.hitMegaBlock(mbId);
      if (!mb) return null;
      megaBlockId = mbId;
      megaBlockHpRemaining = mb.hp;
    } else if (this.board.isSnakeCell(target.row, target.col)) {
      // Snake block: remove from bottom instead of top
      const result = this.board.removeSnakeBottomCube(target.row, target.col);
      if (!result) return null;
      snakeRemovalTarget = { row: result.row, col: target.col };
    } else {
      const removed = this.board.removeTopCube(target.row, target.col);
      if (removed === null) return null;
    }

    orb.ammo--;

    // After clearing a cube, decrement HP on any locked doors matching this color.
    // Mega block hits also count — they use the same color index.
    const unlockedDoors = this.board.hitDoor(orb.colorIndex);

    if (isExhausted(attacker)) {
      attacker.state = 'exhausted';
    }

    return {
      attackerId: attacker.id,
      orbIndex,
      target,
      colorIndex: orb.colorIndex,
      snakeRemovalTarget,
      megaBlockId,
      megaBlockHpRemaining,
      unlockedDoors: unlockedDoors.length > 0 ? unlockedDoors : undefined,
    };
  }
}
